import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import RAIDLog from '../components/RAIDLog';
import AIProjectInsights from '../components/AIProjectInsights';
import ProjectTimeline from '../components/ProjectTimeline';
import FileAttachments from '../components/FileAttachments';
import LinkedProcessMaps from '../components/LinkedProcessMaps';
import ProcessSelector from '../components/ProcessSelector';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  Edit, 
  CheckCircle2, 
  XCircle,
  MessageSquare,
  Loader2,
  Calendar,
  User,
  Users,
  AlertTriangle,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isApprover } = useAuth();
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editedProject, setEditedProject] = useState({});
  
  // Close project dialog
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [finalValue, setFinalValue] = useState('');
  const [finalUnit, setFinalUnit] = useState('');
  const [finalDate, setFinalDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Add task dialog
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskOwner, setTaskOwner] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskDeps, setTaskDeps] = useState([]);

  useEffect(() => {
    fetchProject();
    fetchComments();
    fetchUsers();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await api.get(`/projects/${id}`);
      setProject(response.data);
      setEditedProject(response.data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/comments/project/${id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSaveProject = async () => {
    setSubmitting(true);
    try {
      await api.put(`/projects/${id}`, {
        project_name: editedProject.project_name,
        owner_id: editedProject.owner_id,
        start_date: editedProject.start_date,
        target_end_date: editedProject.target_end_date,
        current_status: editedProject.current_status,
        risk_overlay: editedProject.risk_overlay
      });
      toast.success('Project updated');
      setEditMode(false);
      fetchProject();
    } catch (error) {
      toast.error('Failed to update project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      await api.post('/comments', {
        content: newComment,
        entity_type: 'project',
        entity_id: id
      });
      toast.success('Comment added');
      setNewComment('');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseProject = async () => {
    if (!finalValue) {
      toast.error('Please enter final value');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/projects/${id}/close`, {
        baseline_metric_name: project.value_metrics?.baseline_metric_name,
        baseline_value: project.value_metrics?.baseline_value,
        baseline_unit: project.value_metrics?.baseline_unit,
        expected_value: project.value_metrics?.expected_value,
        expected_unit: project.value_metrics?.expected_unit,
        final_value: parseFloat(finalValue),
        final_unit: finalUnit || project.value_metrics?.baseline_unit,
        final_date: finalDate,
        confidence: 'Realised'
      });
      toast.success('Project completed successfully');
      setShowCloseDialog(false);
      fetchProject();
    } catch (error) {
      toast.error('Failed to close project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTask = async () => {
    if (!taskTitle) {
      toast.error('Task title is required');
      return;
    }
    
    const owner = users.find(u => u.id === taskOwner);
    
    setSubmitting(true);
    try {
      await api.post(`/projects/${id}/tasks`, {
        title: taskTitle,
        owner_id: taskOwner || null,
        owner_name: owner?.name || null,
        due_date: taskDueDate || null,
        status: 'To Do',
        dependencies: taskDeps,
      });
      toast.success('Task added');
      setShowTaskDialog(false);
      setTaskTitle('');
      setTaskOwner('');
      setTaskDueDate('');
      setTaskDeps([]);
      fetchProject();
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTaskStatusChange = async (task, newStatus) => {
    try {
      await api.put(`/projects/${id}/tasks/${task.id}`, {
        ...task,
        status: newStatus
      });
      fetchProject();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const canEdit = () => isApprover();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  if (!project) return null;

  const getProgressValue = () => {
    const statusProgress = {
      'Not Started': 0,
      'In Progress': 50,
      'Blocked': 50,
      'Completed': 100,
      'Cancelled': 0
    };
    return statusProgress[project.current_status] || 0;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {project.project_name}
              </h1>
              <StatusBadge status={project.current_status} />
              <StatusBadge status={project.project_type} />
            </div>
            <p className="text-slate-500 mt-1">
              From idea: {project.idea?.title} • Created {new Date(project.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {canEdit() && !editMode && (
            <Button 
              variant="outline"
              onClick={() => setEditMode(true)}
              data-testid="edit-project-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          {editMode && (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveProject}
                className="bg-[#235894] hover:bg-[#1E4B80]"
                disabled={submitting}
                data-testid="save-project-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </>
          )}
          {canEdit() && project.current_status !== 'Completed' && project.current_status !== 'Cancelled' && (
            <Button 
              onClick={() => setShowCloseDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="close-project-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Complete Project
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-slate-500">Progress</span>
                <span className="text-sm font-medium text-slate-900">{getProgressValue()}%</span>
              </div>
              <Progress value={getProgressValue()} className="h-3" />
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{project.start_date || 'Not set'}</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{project.target_end_date || 'Not set'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Details */}
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Project Name</Label>
                    <Input
                      value={editedProject.project_name}
                      onChange={(e) => setEditedProject({...editedProject, project_name: e.target.value})}
                      data-testid="edit-project-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Owner</Label>
                      <Select 
                        value={editedProject.owner_id} 
                        onValueChange={(v) => setEditedProject({...editedProject, owner_id: v})}
                      >
                        <SelectTrigger data-testid="edit-owner-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={editedProject.current_status} 
                        onValueChange={(v) => setEditedProject({...editedProject, current_status: v})}
                      >
                        <SelectTrigger data-testid="edit-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Not Started">Not Started</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Blocked">Blocked</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="Cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={editedProject.start_date || ''}
                        onChange={(e) => setEditedProject({...editedProject, start_date: e.target.value})}
                        data-testid="edit-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target End Date</Label>
                      <Input
                        type="date"
                        value={editedProject.target_end_date || ''}
                        onChange={(e) => setEditedProject({...editedProject, target_end_date: e.target.value})}
                        data-testid="edit-end-date"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Owner</p>
                      <p className="font-medium text-slate-900">{project.owner_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Team Members</p>
                      <p className="font-medium text-slate-900">{project.team_members?.length || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Start Date</p>
                      <p className="font-medium text-slate-900">
                        {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Target End</p>
                      <p className="font-medium text-slate-900">
                        {project.target_end_date ? new Date(project.target_end_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Value Tracking */}
          <Card>
            <CardHeader>
              <CardTitle>Value Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Baseline */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Baseline</h4>
                  {project.value_metrics?.baseline_metric_name ? (
                    <>
                      <p className="text-sm text-slate-500">{project.value_metrics.baseline_metric_name}</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {project.value_metrics.baseline_value} {project.value_metrics.baseline_unit}
                      </p>
                    </>
                  ) : (
                    <p className="text-slate-400">Not set</p>
                  )}
                </div>

                {/* Expected */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Expected</h4>
                  {project.value_metrics?.expected_value ? (
                    <p className="text-2xl font-bold text-blue-900">
                      {project.value_metrics.expected_value} {project.value_metrics.expected_unit}
                    </p>
                  ) : (
                    <p className="text-slate-400">Not set</p>
                  )}
                </div>

                {/* Final */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-semibold text-emerald-900 mb-2">Final (Realised)</h4>
                  {project.value_metrics?.final_value ? (
                    <>
                      <p className="text-2xl font-bold text-emerald-900">
                        {project.value_metrics.final_value} {project.value_metrics.final_unit}
                      </p>
                      <StatusBadge status={project.value_metrics.confidence || 'Realised'} className="mt-2" />
                    </>
                  ) : (
                    <p className="text-slate-400">Pending completion</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Tasks</CardTitle>
              {canEdit() && (
                <Button 
                  size="sm" 
                  onClick={() => setShowTaskDialog(true)}
                  data-testid="add-task-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Task
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {project.tasks?.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No tasks yet</p>
                ) : (
                  project.tasks?.map((task) => (
                    <div 
                      key={task.id} 
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                    >
                      <Checkbox
                        checked={task.status === 'Done'}
                        onCheckedChange={(checked) => 
                          handleTaskStatusChange(task, checked ? 'Done' : 'To Do')
                        }
                        data-testid={`task-checkbox-${task.id}`}
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${task.status === 'Done' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          {task.owner_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {task.owner_name}
                            </span>
                          )}
                          {task.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {task.due_date}
                            </span>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={task.status} />
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#235894] flex items-center justify-center text-white text-sm font-medium">
                      {comment.user_name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{comment.user_name}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-slate-700 mt-1">{comment.content}</p>
                    </div>
                  </div>
                ))}
                
                {comments.length === 0 && (
                  <p className="text-slate-400 text-center py-4">No comments yet</p>
                )}

                <div className="flex gap-2 mt-4">
                  <Textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1"
                    data-testid="project-comment-input"
                  />
                  <Button 
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submitting}
                    data-testid="add-project-comment-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Project Timeline */}
          <ProjectTimeline 
            project={project} 
            onTaskClick={(task) => {
              // Optionally scroll to task or open edit
              console.log('Task clicked:', task);
            }} 
          />

          {/* File Attachments */}
          <FileAttachments 
            entityType="project" 
            entityId={id} 
            canEdit={canEdit()} 
          />

          {/* Process Maps */}
          <LinkedProcessMaps
            entityType="project"
            entityId={id}
            entityName={project?.project_name}
            readOnly={!canEdit()}
          />

          {/* Linked Process */}
          <Card>
            <CardContent className="pt-5">
              <ProcessSelector
                value={project?.linked_process_id}
                onChange={async (procId, level) => {
                  try {
                    await api.put(`/projects/${id}`, { linked_process_id: procId, linked_process_level: level });
                    setProject(prev => ({ ...prev, linked_process_id: procId, linked_process_level: level }));
                    toast.success(procId ? 'Process linked' : 'Process unlinked');
                  } catch (err) { toast.error('Failed to update linked process'); }
                }}
                label="Linked Process"
                entityType="project"
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Project Insights */}
          <AIProjectInsights projectId={id} />
          
          {/* RAID Log */}
          <RAIDLog projectId={id} canEdit={canEdit()} users={users} />

          {/* Linked Idea */}
          {project.idea && (
            <Card>
              <CardHeader>
                <CardTitle>Source Idea</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => navigate(`/ideas/${project.idea.id}`)}
                >
                  <p className="font-medium text-slate-900">{project.idea.title}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {project.idea.submitter_name} • {project.idea.department}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Close Project Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Project</DialogTitle>
            <DialogDescription>
              Enter the final metrics to close this project
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Metric</p>
              <p className="font-medium">{project.value_metrics?.baseline_metric_name || 'Value'}</p>
              <p className="text-xs text-slate-400 mt-1">
                Baseline: {project.value_metrics?.baseline_value} {project.value_metrics?.baseline_unit} → 
                Expected: {project.value_metrics?.expected_value} {project.value_metrics?.expected_unit}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Final Value *</Label>
                <Input
                  type="number"
                  value={finalValue}
                  onChange={(e) => setFinalValue(e.target.value)}
                  placeholder="Enter final value"
                  data-testid="final-value-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  value={finalUnit}
                  onChange={(e) => setFinalUnit(e.target.value)}
                  placeholder={project.value_metrics?.baseline_unit || 'Unit'}
                  data-testid="final-unit-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Measurement Date</Label>
              <Input
                type="date"
                value={finalDate}
                onChange={(e) => setFinalDate(e.target.value)}
                data-testid="final-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCloseProject}
              disabled={submitting || !finalValue}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-close-project-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Complete Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>
              Add a new task to this project
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Task Title *</Label>
              <Input
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                placeholder="Enter task title"
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Owner</Label>
              <Select value={taskOwner} onValueChange={setTaskOwner}>
                <SelectTrigger data-testid="task-owner-select">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={taskDueDate}
                onChange={(e) => setTaskDueDate(e.target.value)}
                data-testid="task-due-date-input"
              />
            </div>
            {project?.tasks?.length > 0 && (
              <div className="space-y-2">
                <Label>Depends On</Label>
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {project.tasks.map(t => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={taskDeps.includes(t.id)} onChange={e => {
                        setTaskDeps(prev => e.target.checked ? [...prev, t.id] : prev.filter(d => d !== t.id));
                      }} className="rounded" />
                      {t.title}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddTask}
              disabled={submitting || !taskTitle}
              className="bg-[#235894] hover:bg-[#1E4B80]"
              data-testid="confirm-add-task-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
